import { cn } from '@/lib/utils';
import { useCountUp } from '@/hooks/use-count-up';
import { useInView } from '@/hooks/use-in-view';
import {
  fundingGapCallout,
  homepageStats,
  type HomepageStat,
  type StatAccent
} from '@/content/homepage';

const accentClass: Record<StatAccent, string> = {
  cyan: 'text-primary',
  coral: 'text-cta',
  violet: 'text-urgency'
};

function TextStatValue({
  valueLabel,
  accent
}: {
  valueLabel: string;
  accent: StatAccent;
}) {
  return (
    <span
      className={cn(
        'font-display text-5xl font-semibold tracking-tight tabular-nums sm:text-6xl lg:text-7xl',
        accentClass[accent]
      )}
    >
      {valueLabel}
    </span>
  );
}

function NumberStatValue({
  target,
  prefix,
  suffix,
  accent,
  active
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  accent: StatAccent;
  active: boolean;
}) {
  const value = useCountUp({ target, active });
  return (
    <span
      className={cn(
        'font-display text-5xl font-semibold tracking-tight tabular-nums sm:text-6xl lg:text-7xl',
        accentClass[accent]
      )}
    >
      {prefix}
      {value}
      {suffix}
    </span>
  );
}

function TimelineStatValue({
  startYear,
  startLabel,
  endYear,
  endLabel,
  accent
}: {
  startYear: string;
  startLabel: string;
  endYear: string;
  endLabel: string;
  accent: StatAccent;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <span
          className={cn(
            'font-display text-5xl font-semibold tracking-tight tabular-nums sm:text-6xl lg:text-7xl',
            accentClass[accent]
          )}
        >
          {startYear}
        </span>
        <p className="text-foreground/90 mt-2 text-sm leading-relaxed sm:text-[0.95rem]">
          {startLabel}
        </p>
      </div>
      <div>
        <span
          className={cn(
            'font-display text-5xl font-semibold tracking-tight tabular-nums sm:text-6xl lg:text-7xl',
            accentClass[accent]
          )}
        >
          {endYear}
        </span>
        <p className="text-foreground/90 mt-2 text-sm leading-relaxed sm:text-[0.95rem]">
          {endLabel}
        </p>
      </div>
    </div>
  );
}

function StatValue({
  stat,
  active
}: {
  stat: HomepageStat;
  active: boolean;
}) {
  if (stat.kind === 'text') {
    return <TextStatValue valueLabel={stat.valueLabel} accent={stat.accent} />;
  }

  if (stat.kind === 'timeline') {
    return (
      <TimelineStatValue
        startYear={stat.startYear}
        startLabel={stat.startLabel}
        endYear={stat.endYear}
        endLabel={stat.endLabel}
        accent={stat.accent}
      />
    );
  }

  return (
    <NumberStatValue
      target={stat.target}
      prefix={stat.prefix}
      suffix={stat.suffix}
      accent={stat.accent}
      active={active}
    />
  );
}

function StatTile({
  stat,
  active
}: {
  stat: HomepageStat;
  active: boolean;
}) {
  return (
    <article className="border-border/60 flex h-full flex-col border-t pt-8 sm:border-t-0 sm:border-l sm:pl-8 sm:pt-0 sm:odd:border-l-0 sm:odd:pl-0 lg:odd:border-l lg:odd:pl-8 lg:[&:nth-child(3n+1)]:border-l-0 lg:[&:nth-child(3n+1)]:pl-0 xl:[&:nth-child(3n+1)]:border-l xl:[&:nth-child(3n+1)]:pl-8 xl:[&:nth-child(5n+1)]:border-l-0 xl:[&:nth-child(5n+1)]:pl-0">
      <div className={stat.kind === 'timeline' ? undefined : 'min-h-16'}>
        <StatValue stat={stat} active={active} />
      </div>
      {stat.kind !== 'timeline' ? (
        <p className="text-foreground/90 mt-5 text-sm leading-relaxed sm:text-[0.95rem]">
          {stat.description}
        </p>
      ) : null}
      <p className="text-muted-foreground mt-auto pt-6 text-[0.4rem] leading-snug tracking-wide">
        Source: {stat.source}
      </p>
    </article>
  );
}

/**
 * Proof strip — quieter ICONIQ-style stats under initiatives.
 */
export function StatsBand() {
  const [ref, inView] = useInView<HTMLElement>();

  return (
    <section
      ref={ref}
      id="the-problem"
      aria-labelledby="stats-heading"
      className="scroll-mt-24 border-y border-border/70 bg-secondary/50 py-20 sm:py-24"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <p className="text-cta mb-4 text-xs font-semibold tracking-[0.24em] uppercase">
            The problem
          </p>
          <h2
            id="stats-heading"
            className="font-display text-foreground text-3xl font-semibold tracking-tight text-balance sm:text-4xl"
          >
            Thirty years stuck — while other cancers moved on
          </h2>
          <p className="text-muted-foreground mt-5 text-base leading-relaxed sm:text-lg">
            Brain cancer still kills too many of our young people. Survival has barely shifted.
            These figures are why a different funding model cannot wait for conventional cycles.
          </p>
        </div>

        <div className="grid gap-10 sm:grid-cols-2 xl:grid-cols-4 xl:gap-0">
          {homepageStats.map((stat) => (
            <StatTile key={stat.id} stat={stat} active={inView} />
          ))}
        </div>

        <aside className="border-primary/40 mx-auto mt-14 max-w-3xl border-l-2 pl-6 text-center sm:pl-8 sm:text-left">
          <p className="text-muted-foreground text-base leading-relaxed sm:text-lg">
            <span className="text-foreground font-medium">What that means. </span>
            {fundingGapCallout}
          </p>
        </aside>
      </div>
    </section>
  );
}
